import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, Recycle } from "lucide-react";
import { PageHero } from "@/components/layout/PageHero";
import { CAPACITY_EVENT } from "@/components/capacity/useCapacity";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";


export default function AdminPurge() {
  const { t, fmtDate, fmtBytes } = useI18n();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/purge").then(({ data: d }) => {
    setData(d);
    return d;
  }), []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!data?.running) return undefined;
    const id = setInterval(() => load().then((d) => !d.running && window.dispatchEvent(new Event(CAPACITY_EVENT))), 2000);
    return () => clearInterval(id);
  }, [data?.running, load]);
  const run = async () => {
    try {
      await api.post("/admin/purge/run");
      toast.success(t("purge.started"));
      setData((d) => ({ ...d, running: true }));
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  return (
    <div data-testid="admin-purge-page">
      <PageHero eyebrow={t("admin.eyebrow")} title={t("purge.title")}>
        <p className="mt-4 max-w-2xl text-sm text-neutral-400">{t("purge.intro")}</p>
      </PageHero>
      <div className="mx-auto max-w-6xl space-y-6 px-4 pb-16 sm:px-8">
        {!data ? <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" /> : (
          <>
            <section className="card-carbon flex flex-col gap-4 rounded-2xl p-5 sm:flex-row sm:items-center">
              <Recycle className="h-6 w-6 text-[var(--gold)]" />
              <div className="min-w-0 flex-1 text-sm text-neutral-300">
                <p data-testid="purge-status">{data.running ? t("purge.running", { w: data.workers, n: data.backlog }) : t("purge.idle", { h: Math.round(data.interval / 60) })}</p>
                <p className="mt-1 text-xs text-neutral-500" data-testid="purge-method">{t("purge.method", { max: data.max_workers })}</p>
              </div>
              <button type="button" className="btn-red btn-sm" onClick={run} disabled={data.running} data-testid="purge-run-btn">
                {data.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 rtl:-scale-x-100" />} {t("purge.run")}
              </button>
            </section>
            <section className="card-carbon min-w-0 overflow-hidden rounded-2xl">
              {!data.reports.length && <p className="p-5 text-sm text-neutral-400" data-testid="purge-empty">{t("purge.empty")}</p>}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-start text-sm" data-testid="purge-reports">
                  <thead className="text-xs uppercase tracking-wider text-neutral-500">
                    <tr>{["date", "files", "freed", "threads", "workers", "duration", "errors"].map((c) => <th key={c} className="px-4 py-3 text-start font-semibold">{t(`purge.col.${c}`)}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800 text-neutral-200">
                    {data.reports.map((r) => (
                      <tr key={r.id} data-testid={`purge-report-${r.id}`}>
                        <td className="px-4 py-2">{fmtDate(r.started_at)} <span className="text-xs text-neutral-500">· {t(`purge.trigger.${r.trigger}`)}</span></td>
                        <td className="px-4 py-2" data-testid={`purge-report-files-${r.id}`}>{r.files_deleted}</td>
                        <td className="px-4 py-2" data-testid={`purge-report-freed-${r.id}`}>{fmtBytes(r.bytes_freed)}</td>
                        <td className="px-4 py-2" data-testid={`purge-report-threads-${r.id}`}><bdi>{r.threads_deleted || 0} / {r.messages_deleted || 0}</bdi>{r.journal_trimmed > 0 && <span className="block text-[11px] text-neutral-400" data-testid={`purge-report-journal-${r.id}`}>{t("purge.journalTrimmed", { n: r.journal_trimmed })}</span>}</td>
                        <td className="px-4 py-2">{r.workers}</td>
                        <td className="px-4 py-2">{r.duration_s} s</td>
                        <td className={`px-4 py-2 ${r.error_count ? "text-red-300" : ""}`} title={r.errors.join("\n")}>{r.error_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
